import { create } from 'zustand';
import type { ImpersonationMode, ImpersonationTargetSummary, User } from '@court-sync/shared';

const STORAGE_KEY = 'courtsync_impersonation';

interface PersistedImpersonation {
  sessionId: string;
  targetUserId: string;
  targetSummary: ImpersonationTargetSummary;
  mode: ImpersonationMode;
}

interface ImpersonationState {
  sessionId: string | null;
  targetUserId: string | null;
  targetSummary: ImpersonationTargetSummary | null;
  mode: ImpersonationMode | null;
  startImpersonation: (sessionId: string, profile: User, mode: ImpersonationMode) => void;
  syncImpersonationMode: (mode: ImpersonationMode) => void;
  clearImpersonation: () => void;
}

function loadFromSessionStorage(): Pick<
  ImpersonationState,
  'sessionId' | 'targetUserId' | 'targetSummary' | 'mode'
> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { sessionId: null, targetUserId: null, targetSummary: null, mode: null };
    }
    const parsed = JSON.parse(raw) as PersistedImpersonation;
    if (!parsed.sessionId || !parsed.targetUserId || !parsed.targetSummary) {
      return { sessionId: null, targetUserId: null, targetSummary: null, mode: null };
    }
    return {
      sessionId: parsed.sessionId,
      targetUserId: parsed.targetUserId,
      targetSummary: parsed.targetSummary,
      mode:
        parsed.mode === 'read_write'
          ? 'read_write'
          : parsed.mode === 'read_only'
            ? 'read_only'
            : null,
    };
  } catch {
    return { sessionId: null, targetUserId: null, targetSummary: null, mode: null };
  }
}

function persistToSessionStorage(state: {
  sessionId: string | null;
  targetUserId: string | null;
  targetSummary: ImpersonationTargetSummary | null;
  mode: ImpersonationMode | null;
}): void {
  if (!state.sessionId || !state.targetUserId || !state.targetSummary || !state.mode) {
    sessionStorage.removeItem(STORAGE_KEY);
    return;
  }
  sessionStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      sessionId: state.sessionId,
      targetUserId: state.targetUserId,
      targetSummary: state.targetSummary,
      mode: state.mode,
    })
  );
}

const initial = loadFromSessionStorage();

export const useImpersonationStore = create<ImpersonationState>((set) => ({
  sessionId: initial.sessionId,
  targetUserId: initial.targetUserId,
  targetSummary: initial.targetSummary,
  mode: initial.mode,

  startImpersonation: (sessionId, profile, mode) => {
    const targetSummary: ImpersonationTargetSummary = {
      id: profile.id,
      email: profile.email,
      firstName: profile.firstName,
      lastName: profile.lastName,
    };
    const next = {
      sessionId,
      targetUserId: profile.id,
      targetSummary,
      mode,
    };
    persistToSessionStorage(next);
    set(next);
  },

  syncImpersonationMode: (mode) => {
    set((state) => {
      if (!state.sessionId || !state.targetUserId || !state.targetSummary) {
        return state;
      }
      const next = {
        sessionId: state.sessionId,
        targetUserId: state.targetUserId,
        targetSummary: state.targetSummary,
        mode,
      };
      persistToSessionStorage(next);
      return { mode };
    });
  },

  clearImpersonation: () => {
    sessionStorage.removeItem(STORAGE_KEY);
    set({ sessionId: null, targetUserId: null, targetSummary: null, mode: null });
  },
}));

/** For non-React callers (axios interceptors, fetch helpers). */
export function getImpersonationUserId(): string | null {
  return useImpersonationStore.getState().targetUserId;
}

export function getImpersonationSessionId(): string | null {
  return useImpersonationStore.getState().sessionId;
}

export function getImpersonationMode(): ImpersonationMode | null {
  return useImpersonationStore.getState().mode;
}

export function isImpersonating(): boolean {
  return !!useImpersonationStore.getState().targetUserId;
}
